import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import * as LucideIcons from 'lucide-react';
import api from '../utils/api';
import WhatsAppButton from '../components/WhatsAppButton';

// Convert kebab-case to PascalCase for Lucide icons
const toPascalCase = (str) => {
  if (!str) return '';
  return str
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join('');
};

// Dynamic icon component
const DynamicIcon = ({ name, className }) => {
  const pascalName = toPascalCase(name);
  const IconComponent = LucideIcons[pascalName] || LucideIcons[name] || LucideIcons.FileText;
  return <IconComponent className={className} />;
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [hero, setHero] = useState(null);
  const [features, setFeatures] = useState([]);
  const [benefits, setBenefits] = useState([]);
  const [stats, setStats] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCMSData = async () => {
      try {
        const [heroRes, featuresRes, benefitsRes, statsRes, settingsRes] = await Promise.all([
          api.get('/cms/public/hero'),
          api.get('/cms/public/features'),
          api.get('/cms/public/benefits'),
          api.get('/cms/public/stats'),
          api.get('/cms/public/site-settings')
        ]);
        setHero(heroRes.data);
        setFeatures(featuresRes.data);
        setBenefits(benefitsRes.data);
        setStats(statsRes.data);
        
        // Process settings to convert relative URLs to absolute
        const processedSettings = { ...settingsRes.data };
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        
        // Helper to convert URLs
        const convertUrl = (url) => {
          if (!url) return url;
          // If it's a relative /api path, prepend backend URL
          if (url.startsWith('/api')) {
            return `${backendUrl}${url}`;
          }
          // If it's a Google Drive link, convert to direct image URL
          if (url.includes('drive.google.com/file/d/')) {
            const fileId = url.match(/\/d\/([^/]+)/)?.[1];
            if (fileId) {
              return `https://drive.google.com/uc?export=view&id=${fileId}`;
            }
          }
          return url;
        };
        
        // Convert relative URLs to absolute URLs
        processedSettings.logo_url = convertUrl(processedSettings.logo_url);
        processedSettings.favicon_url = convertUrl(processedSettings.favicon_url);
        
        setSettings(processedSettings);
        
        // Update favicon dynamically
        if (processedSettings?.favicon_url) {
          const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
          link.type = 'image/x-icon';
          link.rel = 'shortcut icon';
          link.href = processedSettings.favicon_url;
          document.getElementsByTagName('head')[0].appendChild(link);
        }
        
        // Update page title
        if (processedSettings?.site_name) {
          document.title = processedSettings.site_name;
        }
      } catch (error) {
        console.error('Failed to fetch CMS data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCMSData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  const siteName = settings?.site_name || 'Ontrof';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900" data-testid="landing-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-lg border-b border-white/10" style={{ backgroundColor: 'rgba(6, 8, 12, 0.95)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              {settings?.logo_url ? (
                <img src={settings.logo_url} alt={siteName} className="h-10 w-auto" />
              ) : (
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center">
                  <LucideIcons.Sparkles className="w-6 h-6 text-white" />
                </div>
              )}
              <span className="text-xl font-bold text-white">{siteName}</span>
            </Link>
            
            <div className="hidden md:flex items-center gap-6">
              <Link to="/features" className="text-white/80 hover:text-white text-sm font-medium transition-colors">Features</Link>
              <Link to="/pricing" className="text-white/80 hover:text-white text-sm font-medium transition-colors">Pricing</Link>
              <Link to="/contact" className="text-white/80 hover:text-white text-sm font-medium transition-colors">Contact</Link>
            </div>

            <div className="flex items-center gap-3">
              <Button
                onClick={() => navigate('/register')}
                data-testid="nav-register-btn"
                className="bg-gradient-to-r from-cyan-400 to-blue-500 hover:from-cyan-500 hover:to-blue-600 text-white font-semibold px-5 py-2 rounded-lg shadow-lg shadow-cyan-500/25 transition-all duration-300 hover:shadow-cyan-500/40 hover:scale-105"
              >
                Register
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/login')}
                data-testid="nav-login-btn"
                className="border-2 border-cyan-400 text-cyan-400 hover:bg-cyan-400 hover:text-slate-900 font-semibold px-5 py-2 rounded-lg transition-all duration-300 hover:scale-105"
              >
                Login
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          {hero?.badge_text && (
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/20 rounded-full text-blue-300 text-sm font-medium mb-8 border border-blue-500/30">
              <LucideIcons.Sparkles className="w-4 h-4" />
              {hero.badge_text}
            </div>
          )}
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            {hero?.headline || 'Create Travel Quotations'}
            {hero?.headline_highlight && (
              <span className="block bg-gradient-to-r from-blue-400 to-cyan-300 bg-clip-text text-transparent">
                {hero.headline_highlight}
              </span>
            )}
          </h1>
          
          {hero?.subheadline && (
            <p className="text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto mb-10">
              {hero.subheadline}
            </p>
          )}

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            <Button 
              size="lg"
              className="bg-gradient-to-r from-cyan-400 to-blue-500 hover:from-cyan-500 hover:to-blue-600 text-white text-lg px-8 py-6 rounded-xl shadow-lg shadow-cyan-500/30 transition-all duration-300 hover:shadow-cyan-500/50 hover:scale-105"
              onClick={() => navigate(hero?.primary_cta_link || '/register')}
              data-testid="hero-primary-cta"
            >
              {hero?.primary_cta_text || 'Start Free Trial'}
              <LucideIcons.ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-2 border-cyan-400/60 text-white hover:bg-cyan-400/20 hover:border-cyan-400 text-lg px-8 py-6 rounded-xl transition-all duration-300 hover:scale-105"
              onClick={() => navigate(hero?.secondary_cta_link || '/cherry-holidays/login')}
              data-testid="hero-secondary-cta"
            >
              <LucideIcons.Play className="w-5 h-5 mr-2" />
              {hero?.secondary_cta_text || 'View Demo'}
            </Button>
          </div>

          {/* Stats */}
          {stats.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              {stats.map((stat, idx) => (
                <div key={idx} className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                  <div className="text-3xl font-bold text-white mb-1">{stat.value}</div>
                  <div className="text-slate-400 text-sm">{stat.label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Features Section */}
      {features.length > 0 && (
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900/50" id="features">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Everything You Need to Scale
              </h2>
              <p className="text-slate-400 text-lg max-w-2xl mx-auto">
                Purpose-built for Indian travel agencies and DMCs
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature, idx) => (
                <div 
                  key={idx}
                  className="group bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-blue-500/50 transition-all duration-300"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform">
                    <DynamicIcon name={feature.icon} className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-slate-400">{feature.description}</p>
                </div>
              ))}
            </div>

            <div className="text-center mt-10">
              <Button 
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
                onClick={() => navigate('/features')}
              >
                View All Features
                <LucideIcons.ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Benefits Section */}
      {benefits.length > 0 && (
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                  Why Travel Agencies Choose Us
                </h2>
                <p className="text-slate-400 text-lg mb-8">
                  Built by travel tech experts who understand the unique challenges of Indian DMCs and travel agencies.
                </p>
                <div className="space-y-4">
                  {benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <LucideIcons.CheckCircle className="w-4 h-4 text-green-400" />
                      </div>
                      <span className="text-slate-300">{benefit.text}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-3xl p-8 border border-white/10">
                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-blue-500/30 rounded-2xl flex items-center justify-center">
                      <LucideIcons.Clock className="w-7 h-7 text-blue-300" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-white">5 Minutes</div>
                      <div className="text-slate-400">Average quotation time</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-green-500/30 rounded-2xl flex items-center justify-center">
                      <LucideIcons.BarChart3 className="w-7 h-7 text-green-300" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-white">3x More</div>
                      <div className="text-slate-400">Quotations per day</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-amber-500/30 rounded-2xl flex items-center justify-center">
                      <LucideIcons.IndianRupee className="w-7 h-7 text-amber-300" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-white">GST Ready</div>
                      <div className="text-slate-400">Auto tax calculations</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-r from-blue-600 to-cyan-500 rounded-3xl p-10 sm:p-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Ready to Transform Your Agency?
            </h2>
            <p className="text-blue-100 text-lg mb-8 max-w-2xl mx-auto">
              Join hundreds of travel agencies already using {siteName} to streamline their quotation process.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                size="lg"
                className="bg-white text-blue-600 hover:bg-blue-50 text-lg px-8 py-6 rounded-xl font-semibold"
                onClick={() => navigate('/register')}
                data-testid="cta-register-btn"
              >
                Register Your Agency
                <LucideIcons.ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10 text-lg px-8 py-6 rounded-xl"
                onClick={() => navigate('/contact')}
              >
                Contact Sales
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 px-4 sm:px-6 lg:px-8 border-t border-white/10" style={{ backgroundColor: '#06080C' }}>
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-2">
              {settings?.logo_url ? (
                <img src={settings.logo_url} alt={siteName} className="h-8 w-auto" />
              ) : (
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-lg flex items-center justify-center">
                  <LucideIcons.Sparkles className="w-4 h-4 text-white" />
                </div>
              )}
              <span className="text-white font-semibold">{siteName}</span>
            </Link>
            <div className="text-slate-400 text-sm">
              &copy; {settings?.copyright_text || '2026 Ontrof. All rights reserved.'}
            </div>
            <div className="flex items-center gap-6">
              <Link to="/features" className="text-slate-400 hover:text-white text-sm transition-colors">Features</Link>
              <Link to="/pricing" className="text-slate-400 hover:text-white text-sm transition-colors">Pricing</Link>
              <Link to="/contact" className="text-slate-400 hover:text-white text-sm transition-colors">Contact</Link>
              <Link to="/admin" className="text-slate-400 hover:text-white text-sm transition-colors">Admin</Link>
            </div>
          </div>
        </div>
      </footer>
      
      {/* WhatsApp Floating Button */}
      {settings?.whatsapp_enabled !== false && settings?.whatsapp_number && (
        <WhatsAppButton 
          phoneNumber={settings.whatsapp_number} 
          message={`Hello! I'm interested in ${settings?.site_name || 'your services'}.`}
        />
      )}
    </div>
  );
};

export default LandingPage;
